import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Pencil, ArrowLeft, FileText, BookOpen, CheckCircle2, XCircle } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { PortalLayout } from "@/components/portal-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useCanMutate } from "@/lib/auth";
import { openAssignmentFile } from "@/lib/assignment-file-url";

export const Route = createFileRoute("/teacher/assignments")({ component: AssignmentsPage });

type AssignmentRow = {
  id: string;
  title: string;
  description: string | null;
  max_score: number;
  due_at: string | null;
  class_id: string;
  created_at: string;
  session_id: string | null;
  file_url: string | null;
  file_name: string | null;
};

function AssignmentsPage() {
  const { user } = useAuth();
  const canMutate = useCanMutate();
  const qc = useQueryClient();

  const [selectedClass, setSelectedClass] = React.useState("");
  const [selectedSession, setSelectedSession] = React.useState(""); // "" | "general" | sessionId

  const [createOpen, setCreateOpen] = React.useState(false);
  const [editingAssignment, setEditingAssignment] = React.useState<AssignmentRow | null>(null);
  const [viewingAssignment, setViewingAssignment] = React.useState<AssignmentRow | null>(null);

  const [form, setForm] = React.useState({ title: "", description: "", max_score: 10, due_at: "" });
  const [file, setFile] = React.useState<File | null>(null);

  const classesQ = useQuery({
    queryKey: ["teacher-classes", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.from("classes").select("id, name").eq("teacher_id", user!.id).order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const sessionsQ = useQuery({
    queryKey: ["teacher-sessions", selectedClass],
    enabled: !!selectedClass,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("class_sessions").select("id, starts_at, topic")
        .eq("class_id", selectedClass).order("starts_at");
      if (error) throw error;
      return (data ?? []).map((s, i) => ({ ...s, sessionNumber: i + 1 }));
    },
  });

  const assignmentsQ = useQuery({
    queryKey: ["teacher-assignments-class", selectedClass],
    enabled: !!selectedClass,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("assignments")
        .select("id, title, description, max_score, due_at, class_id, created_at, session_id, file_url, file_name")
        .eq("class_id", selectedClass)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as AssignmentRow[];
    },
  });

  const enrolledQ = useQuery({
    queryKey: ["enrolled-count", selectedClass],
    enabled: !!selectedClass,
    queryFn: async () => {
      const { count } = await supabase
        .from("enrollments").select("*", { count: "exact", head: true })
        .eq("class_id", selectedClass).eq("is_active", true);
      return count ?? 0;
    },
  });

  const submissionsQ = useQuery({
    queryKey: ["teacher-submissions-class", selectedClass],
    enabled: !!selectedClass && !!assignmentsQ.data,
    queryFn: async () => {
      const ids = (assignmentsQ.data ?? []).map(a => a.id);
      if (!ids.length) return [];
      const { data, error } = await supabase
        .from("submissions").select("id, assignment_id, student_id, score")
        .in("assignment_id", ids);
      if (error) throw error;
      return data ?? [];
    },
  });

  const assignmentsBySession = React.useMemo(() => {
    const map = new Map<string, AssignmentRow[]>();
    (assignmentsQ.data ?? []).forEach(a => {
      if (!a.session_id) return;
      const arr = map.get(a.session_id) ?? [];
      arr.push(a);
      map.set(a.session_id, arr);
    });
    return map;
  }, [assignmentsQ.data]);

  const submissionCounts = React.useMemo(() => {
    const map = new Map<string, { submitted: number; total: number }>();
    const total = enrolledQ.data ?? 0;
    (assignmentsQ.data ?? []).forEach(a => {
      const subs = (submissionsQ.data ?? []).filter(s => s.assignment_id === a.id);
      const uniqueStudents = new Set(subs.map(s => s.student_id));
      map.set(a.id, { submitted: uniqueStudents.size, total });
    });
    return map;
  }, [assignmentsQ.data, submissionsQ.data, enrolledQ.data]);

  const filteredAssignments = React.useMemo(() => {
    const all = assignmentsQ.data ?? [];
    if (selectedSession === "general") return all.filter(a => !a.session_id);
    return all.filter(a => a.session_id === selectedSession);
  }, [assignmentsQ.data, selectedSession]);

  const resetForm = () => {
    setForm({ title: "", description: "", max_score: 10, due_at: "" });
    setFile(null);
    setEditingAssignment(null);
  };

  const uploadIfNeeded = async (): Promise<{ url: string | null; name: string | null }> => {
    if (!file) return { url: null, name: null };
    const centerId = (user as { centerId?: string | null } | null)?.centerId;
    if (!centerId) throw new Error("Trung tâm chưa được khởi tạo");
    if (!selectedClass) throw new Error("Hãy chọn lớp trước khi tải file");
    const ext = file.name.split(".").pop() ?? "bin";
    // Path format: {centerId}/{classId}/{uuid}.{ext} — RLS enforces teacher owns classId.
    const path = `${centerId}/${selectedClass}/${crypto.randomUUID()}.${ext}`;
    const { error: upErr } = await supabase.storage.from("assignment-files").upload(path, file);
    if (upErr) throw new Error("Upload file thất bại: " + upErr.message);
    // Bucket is private — store the path; openAssignmentFile() resolves to signed URL.
    return { url: path, name: file.name };
  };


  const createMut = useMutation({
    mutationFn: async () => {
      if (!form.title.trim() || !selectedClass) throw new Error("Hãy nhập tiêu đề");
      const uploaded = await uploadIfNeeded();
      const { error } = await supabase.from("assignments").insert({
        title: form.title.trim(),
        description: form.description.trim() || null,
        class_id: selectedClass,
        max_score: form.max_score,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
        created_by: user?.id ?? null,
        session_id: selectedSession && selectedSession !== "general" ? selectedSession : null,
        file_url: uploaded.url,
        file_name: uploaded.name,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Đã tạo bài tập");
      setCreateOpen(false);
      resetForm();
      qc.invalidateQueries({ queryKey: ["teacher-assignments-class", selectedClass] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMut = useMutation({
    mutationFn: async () => {
      if (!editingAssignment) return;
      let fileUrl: string | null = editingAssignment.file_url;
      let fileName: string | null = editingAssignment.file_name;
      if (file) {
        const uploaded = await uploadIfNeeded();
        fileUrl = uploaded.url;
        fileName = uploaded.name;
      }
      const { error } = await supabase.from("assignments").update({
        title: form.title.trim(),
        description: form.description.trim() || null,
        max_score: form.max_score,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
        file_url: fileUrl,
        file_name: fileName,
      }).eq("id", editingAssignment.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Đã cập nhật");
      setCreateOpen(false);
      resetForm();
      qc.invalidateQueries({ queryKey: ["teacher-assignments-class", selectedClass] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("assignments").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Đã xoá");
      qc.invalidateQueries({ queryKey: ["teacher-assignments-class", selectedClass] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const openEdit = (a: AssignmentRow) => {
    setEditingAssignment(a);
    setForm({
      title: a.title,
      description: a.description ?? "",
      max_score: a.max_score,
      due_at: a.due_at ? format(new Date(a.due_at), "yyyy-MM-dd'T'HH:mm") : "",
    });
    setFile(null);
    setCreateOpen(true);
  };

  const currentSessionLabel = selectedSession === "general"
    ? "Bài tập chung"
    : `Buổi ${sessionsQ.data?.find(s => s.id === selectedSession)?.sessionNumber ?? ""}`;
  const currentSessionTopic = selectedSession !== "general"
    ? sessionsQ.data?.find(s => s.id === selectedSession)?.topic
    : null;

  return (
    <PortalLayout portal="teacher" title="Quản lý bài tập" breadcrumb={[{ label: "Giáo viên", to: "/teacher" }, { label: "Bài tập" }]}>
      {/* Bước 1: Chọn lớp */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={selectedClass} onValueChange={(v) => { setSelectedClass(v); setSelectedSession(""); }}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Chọn lớp..." /></SelectTrigger>
          <SelectContent>
            {(classesQ.data ?? []).map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!selectedClass && (
        <div className="rounded-2xl border-2 border-dashed p-12 text-center text-sm text-muted-foreground">
          Hãy chọn lớp để xem buổi học và bài tập.
        </div>
      )}

      {/* Bước 2: Danh sách buổi */}
      {selectedClass && !selectedSession && (
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Buổi học</h3>
            <Button size="sm" variant="outline" onClick={() => setSelectedSession("general")}>
              📋 Bài tập chung
            </Button>
          </div>
          {sessionsQ.isLoading ? (
            <div className="text-sm text-muted-foreground">Đang tải...</div>
          ) : (sessionsQ.data ?? []).length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed p-12 text-center text-sm text-muted-foreground">
              Lớp này chưa có buổi học nào.
            </div>
          ) : (sessionsQ.data ?? []).map(s => {
            const assignCount = assignmentsBySession.get(s.id)?.length ?? 0;
            return (
              <motion.div key={s.id}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -2 }}
                onClick={() => setSelectedSession(s.id)}
                className={`cursor-pointer rounded-xl border p-4 hover:shadow-md transition-all ${
                  assignCount > 0 ? "bg-[var(--portal-soft)] border-[var(--portal)]/20" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="font-medium">Buổi {s.sessionNumber}</div>
                    {s.topic && <span className="text-xs text-muted-foreground truncate flex items-center gap-1"><BookOpen className="size-3" /> {s.topic}</span>}
                  </div>
                  {assignCount > 0 ? (
                    <Badge className="text-xs" style={{ background: "var(--portal)", color: "white" }}><FileText className="size-3 mr-1" /> {assignCount} bài tập</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-muted-foreground">Chưa có BT</Badge>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Bước 3: BT của buổi đã chọn */}
      {selectedClass && selectedSession && (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelectedSession("")}>
                <ArrowLeft className="size-4" /> Quay lại
              </Button>
              <h3 className="font-display font-semibold">{currentSessionLabel}</h3>
              {currentSessionTopic && (
                <span className="text-sm text-muted-foreground flex items-center gap-1"><BookOpen className="size-3.5" /> {currentSessionTopic}</span>
              )}
            </div>
            <Button disabled={!canMutate} onClick={() => { resetForm(); setCreateOpen(true); }} style={{ background: "var(--portal)", color: "white" }}>
              <Plus className="size-4" /> Tạo bài tập
            </Button>
          </div>

          {filteredAssignments.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed p-12 text-center">
              <FileText className="size-8 mx-auto mb-2 text-muted-foreground" />
              <div className="text-sm text-muted-foreground">Chưa có bài tập nào cho buổi này.</div>
              <Button variant="outline" className="mt-3" disabled={!canMutate} onClick={() => { resetForm(); setCreateOpen(true); }}>
                Tạo bài tập đầu tiên
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAssignments.map(a => {
                const subCount = submissionCounts.get(a.id) ?? { submitted: 0, total: enrolledQ.data ?? 0 };
                return (
                  <motion.div key={a.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="bg-card border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setViewingAssignment(a)}
                  >
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <div className="font-medium min-w-0 truncate">{a.title}</div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="secondary" className="text-[10px]">{subCount.submitted}/{subCount.total} đã nộp</Badge>
                        <Button size="icon" variant="ghost" disabled={!canMutate}
                          onClick={(e) => { e.stopPropagation(); openEdit(a); }}>
                          <Pencil className="size-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" disabled={!canMutate}
                          onClick={(e) => { e.stopPropagation(); if (confirm("Xoá bài tập này?")) deleteMut.mutate(a.id); }}>
                          <Trash2 className="size-3.5 text-rose-600" />
                        </Button>
                      </div>
                    </div>
                    {a.description && <p className="text-sm text-muted-foreground line-clamp-2">{a.description}</p>}
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      {a.due_at && <span>Hạn: {format(new Date(a.due_at), "dd/MM/yyyy HH:mm", { locale: vi })}</span>}
                      {a.file_name && (
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); openAssignmentFile(a.file_url); }}
                          className="text-blue-600 hover:underline">📎 {a.file_name}</button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Dialog tạo/sửa */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!o) resetForm(); setCreateOpen(o); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAssignment ? "Sửa bài tập" : "Tạo bài tập mới"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Tiêu đề</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="VD: Bài tập số 8" /></div>
            <div><Label>Mô tả</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Điểm tối đa</Label><Input type="number" value={form.max_score} onChange={(e) => setForm({ ...form, max_score: Number(e.target.value) })} /></div>
              <div><Label>Hạn nộp</Label><Input type="datetime-local" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} /></div>
            </div>
            <div>
              <Label>File đính kèm</Label>
              <div onClick={() => document.getElementById("assign-file")?.click()}
                className="border-2 border-dashed rounded-xl p-4 text-center cursor-pointer hover:border-[var(--portal)] transition-colors">
                <input id="assign-file" type="file" className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.png,.xlsx,.pptx"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                {file ? (
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <span>📎</span><span className="font-medium">{file.name}</span>
                    <span className="text-muted-foreground text-xs">({(file.size / 1024).toFixed(0)} KB)</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }}
                      className="ml-2 text-muted-foreground hover:text-destructive">✕</button>
                  </div>
                ) : editingAssignment?.file_name ? (
                  <p className="text-sm text-muted-foreground">📎 Đang đính kèm: <span className="font-medium">{editingAssignment.file_name}</span> — bấm để thay file</p>
                ) : (
                  <p className="text-sm text-muted-foreground">📎 Đính kèm tài liệu — PDF, Word, Excel, ảnh (tùy chọn)</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateOpen(false); resetForm(); }}>Huỷ</Button>
            <Button
              onClick={() => editingAssignment ? updateMut.mutate() : createMut.mutate()}
              disabled={createMut.isPending || updateMut.isPending}
              style={{ background: "var(--portal)", color: "white" }}
            >
              {editingAssignment ? "Lưu" : "Tạo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog xem nộp bài */}
      <Dialog open={!!viewingAssignment} onOpenChange={(o) => !o && setViewingAssignment(null)}>
        <DialogContent className="max-w-lg max-h-[75vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tình trạng nộp bài — {viewingAssignment?.title}</DialogTitle>
          </DialogHeader>
          <SubmissionStatusList assignmentId={viewingAssignment?.id} classId={viewingAssignment?.class_id} />
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}

function SubmissionStatusList({ assignmentId, classId }: { assignmentId?: string; classId?: string }) {
  const statusQ = useQuery({
    queryKey: ["submission-status", assignmentId],
    enabled: !!assignmentId && !!classId,
    queryFn: async () => {
      const { data: enrolls } = await supabase
        .from("enrollments").select("student_id").eq("class_id", classId!).eq("is_active", true);
      const studentIds = (enrolls ?? []).map((e) => e.student_id);
      if (!studentIds.length) return { students: [], submissions: [] };
      const [{ data: profiles }, { data: subs }] = await Promise.all([
        supabase.from("profiles").select("id, display_name, email").in("id", studentIds),
        supabase.from("submissions").select("student_id, content, file_url, file_name, submitted_at, score")
          .eq("assignment_id", assignmentId!),
      ]);
      return { students: profiles ?? [], submissions: subs ?? [] };
    },
  });

  if (statusQ.isLoading) return <div className="text-sm text-muted-foreground">Đang tải...</div>;

  const subMap = new Map((statusQ.data?.submissions ?? []).map((s) => [s.student_id, s]));
  const students = statusQ.data?.students ?? [];
  const submitted = students.filter((s) => subMap.has(s.id));
  const notSubmitted = students.filter((s) => !subMap.has(s.id));

  return (
    <div className="space-y-4">
      <div className="flex gap-3 text-sm">
        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="size-3 mr-1" /> Đã nộp: {submitted.length}</Badge>
        <Badge variant="destructive"><XCircle className="size-3 mr-1" /> Chưa nộp: {notSubmitted.length}</Badge>
      </div>

      {submitted.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Đã nộp</p>
          <div className="space-y-1.5">
            {submitted.map((s) => {
              const sub = subMap.get(s.id)!;
              return (
                <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg border bg-emerald-50/30">
                  <div>
                    <div className="text-sm font-medium">{s.display_name ?? s.email.split("@")[0]}</div>
                    <div className="text-xs text-muted-foreground">
                      Nộp lúc {format(new Date(sub.submitted_at), "HH:mm dd/MM", { locale: vi })}
                      {sub.score !== null && ` · ${sub.score} điểm`}
                    </div>
                  </div>
                  {sub.file_url && (
                    <a href={sub.file_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline">📎 {sub.file_name ?? "File"}</a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {notSubmitted.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Chưa nộp</p>
          <div className="space-y-1.5">
            {notSubmitted.map((s) => (
              <div key={s.id} className="flex items-center p-2.5 rounded-lg border opacity-60">
                <div className="text-sm">{s.display_name ?? s.email.split("@")[0]}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
